import React, {FC} from "react";
import {Button, Col, Divider, Row, Table, Tooltip, Space, Badge, Statistic} from "antd";
import {ColumnProps} from "antd/lib/table";
import moment from "moment";
import {CheckCircleOutlined, InfoCircleTwoTone} from "@ant-design/icons";
import {gold, green, grey, orange, red} from "@ant-design/colors";
import {renderAmount, renderBooleanAsCheckmark} from "../../../views/TableColumnRenderers/TableColumnRenderers";
import {useCurrency} from "../../../helpers/currencyHelper";
import {KnownError, TableType} from "./SwissQrCamtFIleUpload";

const knownError: KnownError = {
    overpaid: {
        msg: "Überbezahlt",
        description: "Der Betrag ist zu hoch. Der Differenzbetrag wird als Guthaben verbucht.",
    },
    underpaid: {
        msg: "Unterbezahlt",
        description: "Der Betrag ist zu niedrig und wird als Teilzahlung verbucht.",
    },
    paid: {msg: "Bezahlt", description: "Der Betrag stimmt überein"},
    ambiguous: {
        msg: "Zu klären",
        description: "Nicht verbucht. Bitte in der Import-Historie über „Klären“ abschließen.",
    },
    "error - invoice not found": {
        msg: "Rechnung nicht gefunden",
        description: "Kann keiner Rechnung zugeordnet werden und wird daher ignoriert.",
    },
    "error - duplicate transaction": {
        msg: "Doppelte Transaktion",
        description: "Diese Transaktion kommt mehrfach in der Datei vor und wird daher ignoriert.",
    },
};

type ImportSummaryProps = {
    result: Array<TableType>;
    onClose: () => void;
};

type ResultCategory = "paid" | "overpaid" | "underpaid" | "ambiguous" | "error";

/**
 * Kategorisiert einen Result-String in eine der Hauptkategorien.
 */
const categorizeResult = (result: string): ResultCategory => {
    if (result === "paid") return "paid";
    if (result === "overpaid") return "overpaid";
    if (result === "underpaid") return "underpaid";
    if (result === "ambiguous") return "ambiguous";
    return "error";
};

const ResultBadge: FC<{result: keyof KnownError | string}> = ({result}) => {
    const isKnownErrorKey = (key: string): key is keyof KnownError => {
        return key in knownError;
    };

    if (!isKnownErrorKey(result)) {
        return <>{result}</>;
    }

    const badgeColor =
        result === "paid"
            ? green[3]
            : result === "overpaid"
            ? orange[3]
            : result === "underpaid"
            ? red[3]
            : result === "ambiguous"
            ? gold[3]
            : grey[0];

    return (
        <Tooltip title={knownError[result].description}>
            <Space size={5}>
                <InfoCircleTwoTone />
                <Badge count={knownError[result].msg} color={badgeColor} />
            </Space>
        </Tooltip>
    );
};

const ImportSummary: FC<ImportSummaryProps> = ({result, onClose}) => {
    const currency = useCurrency();

    const countByCategory = result.reduce(
        (acc, record) => {
            const category = categorizeResult(record.result);
            acc[category] = (acc[category] || 0) + 1;
            return acc;
        },
        {paid: 0, overpaid: 0, underpaid: 0, ambiguous: 0, error: 0} as Record<ResultCategory, number>,
    );

    // Gesamtbetrag zugeordnet: Summe aller amount wo der Betrag tatsächlich zugeordnet wurde.
    // Mehrdeutige Eingänge (ambiguous) werden nicht gebucht und daher NICHT mitgezählt.
    const totalAssigned = result.reduce((sum, record) => {
        const category = categorizeResult(record.result);
        if (category !== "error" && category !== "ambiguous") {
            return sum + (record.amount || 0);
        }
        return sum;
    }, 0);

    // Als Guthaben verbucht: Summe der creditAmount-Werte bei overpaid Records
    const totalCredit = result.reduce((sum, record) => {
        if (categorizeResult(record.result) === "overpaid" && record.creditAmount) {
            return sum + Number(record.creditAmount);
        }
        return sum;
    }, 0);

    const columns: Array<ColumnProps<TableType>> = [
        {
            dataIndex: "amount",
            title: "Eingegangener Betrag",
            render: (text: number) => ({
                displayName: "amount",
                children: <>{renderAmount(text)}</>,
            }),
        },
        {
            dataIndex: "originalOpenAmount",
            title: "Offener Betrag",
            render: (_text: unknown, record: TableType) => ({
                displayName: "openAmount",
                children: record.originalOpenAmount != null
                    ? renderAmount(Number(record.originalOpenAmount))
                    : "-",
            }),
        },
        {
            dataIndex: "valutaDate",
            title: "Valuta",
            render: (text: string) => ({
                displayName: "valutaDate",
                children: <>{moment(text).format("L")}</>,
            }),
        },
        {
            dataIndex: "swissQrRef",
            title: "QR-Referenz",
            render: (text: string) => ({
                displayName: "swissQrRef",
                children: <>{text}</>,
            }),
        },
        {
            dataIndex: "successful",
            title: "Valide",
            render: (text: boolean) => ({
                displayName: "successful",
                children: <>{renderBooleanAsCheckmark(text)}</>,
            }),
        },
        {
            dataIndex: "result",
            title: "Ergebnis",
            render: (text: string) => ({
                displayName: "result",
                children: <ResultBadge result={text} />,
            }),
        },
    ];

    return (
        <div>
            <Divider orientation="left">
                <Space>
                    <CheckCircleOutlined style={{color: green[5]}} />
                    {"Import abgeschlossen"}
                </Space>
            </Divider>

            <Row gutter={[16, 16]} style={{marginBottom: 24}}>
                <Col span={4}>
                    <Statistic title="Transaktionen gesamt" value={result.length} />
                </Col>
                <Col span={4}>
                    <Statistic
                        title="Bezahlt"
                        value={countByCategory.paid}
                        valueStyle={{color: green[5]}}
                    />
                </Col>
                <Col span={4}>
                    <Statistic
                        title="Überbezahlt"
                        value={countByCategory.overpaid}
                        valueStyle={{color: orange[5]}}
                    />
                </Col>
                <Col span={4}>
                    <Statistic
                        title="Unterbezahlt"
                        value={countByCategory.underpaid}
                        valueStyle={{color: red[5]}}
                    />
                </Col>
                <Col span={4}>
                    <Statistic
                        title="Zu klären"
                        value={countByCategory.ambiguous}
                        valueStyle={{color: gold[5]}}
                    />
                </Col>
                <Col span={4}>
                    <Statistic
                        title="Fehler"
                        value={countByCategory.error}
                        valueStyle={{color: grey[3]}}
                    />
                </Col>
            </Row>

            <Row gutter={[16, 16]} style={{marginBottom: 24}}>
                <Col span={6}>
                    <Statistic
                        title="Gesamtbetrag zugeordnet"
                        value={totalAssigned}
                        precision={2}
                        suffix={currency.short}
                    />
                </Col>
                <Col span={6}>
                    <Statistic
                        title="Als Guthaben verbucht"
                        value={totalCredit}
                        precision={2}
                        suffix={currency.short}
                        valueStyle={totalCredit > 0 ? {color: orange[5]} : undefined}
                    />
                </Col>
            </Row>

            <Divider orientation="left">{"Details"}</Divider>

            <Table<TableType>
                size="small"
                columns={columns}
                dataSource={result}
                pagination={false}
                rowKey={(record) => record.swissQrRef || String(record.amount)}
            />

            <div style={{marginTop: 24, textAlign: "right"}}>
                <Button type="primary" onClick={onClose}>
                    {"Schliessen"}
                </Button>
            </div>
        </div>
    );
};

export default ImportSummary;
